import { Component, OnInit, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExportDataService } from '../../service/export-data.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-export-data-view',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './export-data-view.html',
  styleUrl: './export-data-view.scss'
})
export class ExportDataViewComponent implements OnInit {

  transacciones = signal<any[]>([]);
  transaccionSeleccionada = signal<any | null>(null);
  generandoArchivo = signal(false);

  cargandoTransacciones = signal(false);

  fechaInicial = signal<string | null>(null);
  fechaFinal = signal<string | null>(null);

  codigoInicial = signal('');
  codigoFinal = signal('');

  nombreArchivo = signal('');

  usuarioPk = signal<number | null>(null);

  descripcionLarga = computed(() =>
    this.valorCampo(this.transaccionSeleccionada(), 'descripcion_Larga', 'Descripcion_Larga', 'descripcion_larga')
  );

  mostrarFechas = computed(() =>
    this.valorCampo(this.transaccionSeleccionada(), 'pide_Fechas', 'Pide_Fechas', 'pide_fechas') === 'S'
  );

  mostrarCodigos = computed(() =>
    this.valorCampo(this.transaccionSeleccionada(), 'pide_Codigos', 'Pide_Codigos', 'pide_codigos') === 'S'
  );

  tituloCodigos = computed(() =>
    this.valorCampo(this.transaccionSeleccionada(), 'titulo_Codigos', 'Titulo_Codigos', 'titulo_codigos') || 'Código'
  );

  funcionSeleccionada = computed(() =>
    this.valorCampo(this.transaccionSeleccionada(), 'funcion', 'Funcion', 'funcion')
  );

  constructor(private exportDataService: ExportDataService,
    private router: Router) { }

  ngOnInit(): void {
    this.usuarioPk.set(this.getLoggedUserId());
    this.cargarTransacciones();
  }

  cargarTransacciones(): void {
    const usuarioPk = this.usuarioPk();
    if (!usuarioPk) {
      alert('No se encontró el usuario de la sesión.');
      return;
    }

    this.cargandoTransacciones.set(true);
    this.transacciones.set([]);
    this.transaccionSeleccionada.set(null);

    this.exportDataService.getTransacciones(usuarioPk).subscribe({
      next: (data) => {
        this.transacciones.set(data || []);
        this.cargandoTransacciones.set(false);
      },
      error: (err) => {
        console.error('Error cargando transacciones', err);
        alert('Error cargando transacciones');
        this.cargandoTransacciones.set(false);
      }
    });
  }

  cambiarTransaccion(item: any): void {
    this.transaccionSeleccionada.set(item);

    if (!item) return;

    const codigoInicial = this.valorCampo(item, 'codigo_Inicial', 'Codigo_Inicial', 'codigo_inicial');
    const codigoFinal = this.valorCampo(item, 'codigo_Final', 'Codigo_Final', 'codigo_final');

    const descripcion = item.descripcion || item.Descripcion || '';

    if (this.mostrarFechas()) {
      const fechas = this.obtenerFechasMesActual();
      this.fechaInicial.set(fechas.inicio);
      this.fechaFinal.set(fechas.fin);
    } else {
      this.fechaInicial.set(null);
      this.fechaFinal.set(null);
    }

    if (this.mostrarCodigos()) {
      this.codigoInicial.set(codigoInicial ?? '');
      this.codigoFinal.set(codigoFinal ?? '');
    } else {
      this.codigoInicial.set('');
      this.codigoFinal.set('');
    }

    const hoy = new Date();
    const yyyy = hoy.getFullYear();
    const mm = String(hoy.getMonth() + 1).padStart(2, '0');
    const dd = String(hoy.getDate()).padStart(2, '0');

    this.nombreArchivo.set(`${descripcion} Generado el ${yyyy}${mm}${dd}`);
  }

  obtenerFechasMesActual(): { inicio: string; fin: string } {
    const hoy = new Date();
    const anio = hoy.getFullYear();
    const mes = hoy.getMonth();

    const primerDia = new Date(anio, mes, 1);
    const ultimoDia = new Date(anio, mes + 1, 0);

    return {
      inicio: this.formatearFechaInput(primerDia),
      fin: this.formatearFechaInput(ultimoDia)
    };
  }

  formatearFechaInput(fecha: Date): string {
    const yyyy = fecha.getFullYear();
    const mm = String(fecha.getMonth() + 1).padStart(2, '0');
    const dd = String(fecha.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  valorCampo(obj: any, camel: string, pascal: string, snake?: string): string {
    if (!obj) return '';
    return obj[camel] ?? obj[pascal] ?? (snake ? obj[snake] : '') ?? '';
  }

  procesar(): void {
    if (!this.transaccionSeleccionada()) {
      alert('Seleccione una información para exportar');
      return;
    }

    const usuarioPk = this.usuarioPk();
    if (!usuarioPk) {
      alert('No se encontró el usuario de la sesión.');
      return;
    }

    const fechaInicial = this.fechaInicial();
    const fechaFinal = this.fechaFinal();
    const codigoInicial = this.codigoInicial();
    const codigoFinal = this.codigoFinal();

    if (this.mostrarFechas()) {
      if (fechaInicial && fechaFinal && fechaInicial > fechaFinal) {
        alert('La fecha inicial no puede ser mayor que la fecha final');
        return;
      }
    }

    if (this.mostrarCodigos()) {
      if (codigoInicial && codigoFinal && codigoInicial > codigoFinal) {
        alert(
          `${this.tituloCodigos()} inicial no puede ser mayor que ${this.tituloCodigos()} final`
        );
        return;
      }
    }

    const request = {
      fechaInicial: this.mostrarFechas()
        ? this.formatearFecha(fechaInicial)
        : null,
      fechaFinal: this.mostrarFechas()
        ? this.formatearFecha(fechaFinal)
        : null,
      codigoInicial: this.mostrarCodigos() ? (codigoInicial ?? '') : '',
      codigoFinal: this.mostrarCodigos() ? (codigoFinal ?? '') : '',
      funcion: this.funcionSeleccionada(),
      segUsuario: usuarioPk,
      segTransaccion: 46,
      segDatosAnteriores: ''
    };

    this.generandoArchivo.set(true);

    this.exportDataService.exportar(request).subscribe({
      next: (data) => {
        if (!data || data.length === 0) {
          this.generandoArchivo.set(false);
          alert('No hay datos para exportar');
          return;
        }

        try {
          const worksheet = XLSX.utils.json_to_sheet(data);
          const workbook = XLSX.utils.book_new();

          XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

          XLSX.writeFile(
            workbook,
            `${this.limpiarNombreArchivo(this.nombreArchivo())}.xlsx`
          );
        } finally {
          this.generandoArchivo.set(false);
        }
      },
      error: (err) => {
        console.error('Error exportando datos', err);
        this.generandoArchivo.set(false);
        alert('Error exportando datos');
      }
    });
  }
  

  actualizarFechaInicial(fecha: string): void {
    this.fechaInicial.set(fecha || null);
  }

  actualizarFechaFinal(fecha: string): void {
    this.fechaFinal.set(fecha || null);
  }

  formatearFecha(fecha: string | null): string | null {
    if (!fecha) return null;
    return `${fecha} 00:00:00`;
  }

  limpiarNombreArchivo(nombre: string): string {
    return nombre.replace(/[\\/:*?"<>|]/g, '').trim();
  }

  private getLoggedUserId(): number | null {
    const raw = localStorage.getItem('authUser');
    if (!raw) return null;

    try {
      const user = JSON.parse(raw);
      return Number(user.pkUsuario ?? user.id ?? user.pk ?? user.codigo) || null;
    } catch {
      return null;
    }
  }

  limpiar(): void {
    this.fechaInicial.set(null);
    this.fechaFinal.set(null);
    this.codigoInicial.set('');
    this.codigoFinal.set('');
    this.nombreArchivo.set('');
    this.transaccionSeleccionada.set(null);
  }
  salir(): void {
    this.router.navigate(['/home']);
  }
}
